import { Link2 } from 'lucide-react';
import { copyToClipboard } from '../lib/clipboard.js';
import { toast } from './Toast.js';

/**
 * CTSK-354 — "Copy short URL" icon button for list rows / tiles.
 *
 * Writes the fully-qualified short URL (origin + shortPath, e.g.
 * `http://192.168.153.164:5173/pj/CTSK`) to the clipboard via the
 * shared copyToClipboard wrapper (CTSK-281/282 — includes the
 * execCommand fallback for http:// Vault) and confirms with a toast
 * carrying the exact copied URL.
 *
 * A real <button> (keyboard-accessible, focusable) — never a div.
 * Consumers place it inside row action cells / tile rows; click
 * events don't bubble (rows often have their own onClick navigation).
 *
 * Short routes (CTSK-235 set, completed in this ship):
 *   /wi/:key → workitems  ·  /pj/:key → projects
 *   /nt/:slug → nests     ·  /dc/:id → documents  ·  /ct/:slug → tools
 */
export function CopyShortUrlButton({
  shortPath,
  label,
  size = 13,
  style,
}: {
  /** Short-route path starting '/', e.g. `/pj/CTSK`. */
  shortPath: string;
  /** Human name of the resource, used in the aria-label. */
  label: string;
  /** Icon size in px (default 13 — matches row-action density). */
  size?: number;
  /** Optional style overrides for host-surface density tweaks. */
  style?: React.CSSProperties;
}) {
  async function onCopy(e: React.MouseEvent) {
    // Rows/tiles frequently navigate on click — copying must not.
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}${shortPath}`;
    const ok = await copyToClipboard(url);
    if (ok) toast.success(`Copied: ${url}`);
    else toast.error('Could not copy — your browser blocked clipboard access');
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={`Copy short URL for ${label}`}
      title={`Copy short URL: ${shortPath}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        background: 'transparent',
        border: '1px solid transparent',
        borderRadius: 4,
        cursor: 'pointer',
        color: 'var(--text-muted)',
        ...style,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
    >
      <Link2 size={size} strokeWidth={1.75} aria-hidden />
    </button>
  );
}
